<script setup>
import { useEcomTracking } from '~/composables/gtm/useEcomTracking'

const props = defineProps({
  slide: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: false,
  },
  parentType: {
    type: String,
    required: false,
  },
})

const { t } = useI18n()
const seoStore = useSeoStore()
const { trackSelectItem } = useEcomTracking()

const teaser = ref(null)
const targetIsVisible = useElementVisibility(teaser)
const { availabilities, isAvailable, parentPrice } = useAvailability(targetIsVisible, props.slide)

const product = computed(() => {
  return {
    ...props.slide,
  }
})

const price = computed(() => {
  if (parentPrice.value) {
    return parentPrice.value
  }
  if (props.slide?.price?.value) {
    return props.slide.price
  }
  if (availabilities.value.length > 0) {
    return availabilities.value[0]
  }
  return '-'
})

const { badges: productBadges } = useProductBadges(
  computed(() => {
    return (
      props.slide?.tags
      || props.slide?.product?.tags
      || props.slide?.image?.tags
      || []
    )
  }),
  { price },
)

function handleSelectItem() {
  trackSelectItem(
    product.value,
    props.parentType,
    seoStore.pageTitle,
    props.index,
    price.value,
  )
}

const buttonLabel = computed(() => {
  if (props.slide.image?.preorder) {
    return t('product.picker.preOrder')
  }
  return t('product.teaser.discover')
})
</script>

<template>
  <div
    ref="teaser"
    class="d-flex flex-column slide"
    data-cy="product-teaser-detail"
  >
    <div class="align-self-stretch slide__image">
      <NuxtLink
        :to="slide?.link?.to || '#'"
        @click="handleSelectItem"
      >
        <ProductImage
          :image="slide?.image ? slide.image.image : slide.images[0]"
          :sold-out="!isAvailable"
          :product-title="slide.title"
          :pre-order="slide.image?.preorder"
          sizes="sm:340px md:360px lg:465px"
          :badges="productBadges"
        />
      </NuxtLink>
    </div>
    <div class="d-flex align-items-start flex-column slide__content">
      <div class="slide__product">
        <div
          class="equal-small-2 equal-small-2--bold-uppercase slide__type"
        >
          {{ slide.type }}
        </div>
        <div class="slide__product-info">
          <NuxtLink
            :to="slide?.link?.to || '#'"
            class="slide__title-link"
            @click="handleSelectItem"
          >
            <h3
              :id="slugifyString(slide.title)"
              class="scale-2 scale-2--bold-uppercase slide__title"
            >
              {{ slide.title }}
            </h3>
          </NuxtLink>
          <div
            v-if="slide.info"
            class="equal-base equal-base--tighter-bold-uppercase slide__info"
          >
            {{ slide.info }}
          </div>
        </div>
        <Quotation
          v-if="slide.quotation"
          class="equal-base equal-base--tighter-serif slide__quotation"
          :text="slide.quotation"
        />
      </div>
      <div class="d-flex flex-column w-100 mt-auto">
        <div class="d-flex flex-column">
          <div
            v-if="price?.action"
            class="equal-base equal-base--tighter slide__price-invalid"
          >
            {{ price?.currency }}
            {{ price?.original_incl_tax
            }}<span v-if="slide.structure !== 'standalone'">
              / 100g</span>
          </div>
          <div
            class="scale-1 scale-1--bold slide__price"
            :class="{ 'slide__price--offer': price?.action }"
          >
            {{ price?.currency }}
            {{
              price?.action
                ? price?.incl_tax
                : price?.original_incl_tax
            }}
            <span v-if="slide.structure !== 'standalone'">
              / 100g</span>
          </div>
        </div>
        <div class="d-flex slide__buttons">
          <FormButton
            class="align-self-end l-button--secondary l-button--outlined slide__link"
            :label="buttonLabel"
            :to="slide.link?.to"
            component="NuxtLink"
          />
          <div
            v-if="isAvailable"
            class="align-self-end flex-shrink-0 slide__shopping-cart"
          >
            <ProductPicker
              :product="product"
              :availabilities="availabilities"
              placement="bottom"
              :backdrop="false"
              :size40="false"
              parent="teaser_detail"
              :parent-title="seoStore.pageTitle"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~/assets/scss/variables.scss';
@import 'bootstrap/scss/mixins';

.slide {
  height: 100%;
  width: 100%;
  position: relative;
  background-color: $color-dark-03;
  text-align: left;
  .product-image {
    overflow: hidden;
    :deep(.product-image__image) {
      transition: 0.5s ease;
    }
  }
  &:hover,
  &:focus,
  &:active {
    background-color: $color-dark-02;
    transition: 0.5s ease;
    .product-image:deep(.product-image__image) {
      transform: scale(1.05);
      transition: 0.5s ease;
    }
  }
  &--horizontal {
    display: flex;
    flex-direction: row !important;
    align-items: center;
    justify-content: space-between;

    .slide__image {
      width: 50%;
    }

    .slide__content {
      width: 50%;
    }
  }

  &___image {
    max-height: 213px;
    overflow: hidden;
  }

  &__content {
    height: 100%;
    padding: 25px;
  }

  &__product {
    width: 100%;
    margin-bottom: 20px;
  }

  &__type {
    color: $color-disabled;
    @include media-breakpoint-down(640) {
      margin-bottom: 1px;
    }
  }

  &__title {
    margin-right: 10px;
  }

  &__info {
    margin-top: 10px;
    color: $color-disabled;
  }

  &__quotation {
    margin-top: 25px;

    @include media-breakpoint-down(640) {
      margin-top: 20px;
    }
  }

  &__price {
    &--offer {
      color: #c62424;
    }
  }

  &__price-invalid {
    color: #999999;
    text-decoration: line-through;
  }

  &__buttons {
    display: flex;
  }

  &__link {
    margin-top: 25px;
    max-width: min(240px, calc(100% - 60px));
    min-width: unset;
    width: 100%;
  }

  &__shopping-cart {
    margin-left: 10px;
  }
}
</style>
